// Display helpers for the dashboard. Pure (no imports beyond a type), so the
// unit tests can load this file directly under plain Node.
import type { PaymentRequestRow } from "./types";

const nf0 = new Intl.NumberFormat("en-SG");
const nf = new Intl.NumberFormat("en-SG", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** 1268 -> "1,268". */
export function count(n: number): string {
  return nf0.format(n);
}

/** 1840 -> "S$1,840.00". */
export function money(n: number): string {
  return `S$${nf.format(n)}`;
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function clock(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** "just now", "12 min ago", "3 h ago", "yesterday", "4 days ago", "12 Sep". */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const t = new Date(iso);
  const ms = now - t.getTime();
  if (ms < MIN) return "just now";
  if (ms < HOUR) return `${Math.floor(ms / MIN)} min ago`;
  // Under a day it's hours, even across midnight: 5 h ago, not "yesterday".
  if (ms < DAY) return `${Math.floor(ms / HOUR)} h ago`;
  const days = Math.round((startOfDay(new Date(now)) - startOfDay(t)) / DAY);
  if (days <= 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return t.toLocaleDateString("en-SG", { day: "numeric", month: "short" });
}

/** "today 14:02", "yesterday 14:02", "4 days ago", "12 Sep" — for paid rows. */
export function paidAt(iso: string, now: number = Date.now()): string {
  const t = new Date(iso);
  const days = Math.round((startOfDay(new Date(now)) - startOfDay(t)) / DAY);
  if (days <= 0) return `today ${clock(t)}`;
  if (days === 1) return `yesterday ${clock(t)}`;
  return timeAgo(iso, now);
}

/** The small grey line under a row's title: when it was paid or created. */
export function whenLabel(r: PaymentRequestRow, now: number = Date.now()): string {
  return r.status === "confirmed" && r.confirmed_at
    ? paidAt(r.confirmed_at, now)
    : timeAgo(r.created_at, now);
}

/** `6591234567` as `+65 9123 4567`; any other number with a plus in front. */
export function formatSgNumber(digits: string): string {
  const m = /^65(\d{4})(\d{4})$/.exec(digits.replace(/\D/g, ""));
  return m ? `+65 ${m[1]} ${m[2]}` : `+${digits.replace(/\D/g, "")}`;
}

/** Seconds as `9:42`. */
export function countdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
